import { useSelector } from 'react-redux';
import { getLoggedInUserSubscription, getUser } from '../../store/iam/selectors';
import { useFetchSubscriptionPlansQuery, useFetchSubscriptionQuery } from '../service';
import { useMemo } from 'react';
import { getPlanDetails } from '../utils';
import { Plan, Subscription, SubscriptionState, UserSubscription } from '../types';
import moment from 'moment';

/**
 * Adds the plan details for a subscription from the jwt token
 * @param planDetails
 * @param loggedInSubscription
 * @param subscription
 * @returns
 */
function patchSubscription(planDetails: Plan, loggedInSubscription: Record<string, any>, subscription: Subscription, user: any): UserSubscription {
    const patchedSubscription = { ...loggedInSubscription };

    patchedSubscription.plan_details = { ...planDetails };
    // The endpoint is the truth about the state; the claim is a copy that only changes on the next
    // token refresh. A subscription that ends at the provider - it expires, or support terminates it -
    // changes nothing in the token, and the claim then still says active long after it is not.
    patchedSubscription.status = subscription.status;
    patchedSubscription.current_period_ends_at = subscription.current_period_ends_at;
    patchedSubscription.trial_ends_at = subscription.trial_ends_at;

    if (user && user.override_plan && user.override_plan.allow_white_label_reports) {
        patchedSubscription.plan_details.settings.allow_white_label_reports = user.override_plan.allow_white_label_reports;
    }

    return patchedSubscription as UserSubscription;
}

/**
 * Hook that returns the logged in user subscription using the plan code from the JWT Token and details from plans endpoint
 * @returns
 */
export default function useGetSubscription() {
    const { data: plans, isLoading: isLoadingPlans } = useFetchSubscriptionPlansQuery();
    const { data: subscription, isLoading: isLoadingSubscription } = useFetchSubscriptionQuery();

    // TODO Refactor this after IAM refactor
    const loggedInSubscription = useSelector((state) => getLoggedInUserSubscription(state));
    // TODO Refactor this after IAM Refactor
    const user = useSelector((state) => getUser(state));

    // The endpoint's plan code, falling back to the claim's only when there is no subscription row to
    // read - otherwise a plan changed at the provider would show its old name and price here until the
    // next token refresh. Both codes come from the same catalog, so the lookup is the same either way.
    const planCode = subscription?.plan_code || loggedInSubscription?.plan_code;
    const loggedInUserPlanDetails = plans && planCode ? getPlanDetails(plans, planCode) : undefined;

    const hasData = !!subscription && !!loggedInUserPlanDetails;

    // Combine plan details from jwt subscription with endpoint subscription
    const patchedSubscription = useMemo(
        () => (hasData ? patchSubscription(loggedInUserPlanDetails, loggedInSubscription, subscription, user) : undefined),
        [hasData, loggedInUserPlanDetails, loggedInSubscription, subscription, user]
    );

    const isLoading = isLoadingPlans || isLoadingSubscription;
    const isInFreeTrial = !!patchedSubscription && !!patchedSubscription.trial_ends_at && moment(patchedSubscription.trial_ends_at).isAfter(moment());

    // Taken from the endpoint answer and not from the JWT claim: the claim only changes on the next
    // token refresh, and this decides whether the customer is offered a purchase or a plan change.
    const status = subscription?.status;
    const currentPeriodEnd = subscription?.current_period_ends_at;
    const subscriptionState: SubscriptionState =
        status === 'active'
            ? 'active'
            : status === 'canceled' && !!currentPeriodEnd && moment(currentPeriodEnd).isAfter(moment())
                ? 'cancelled'
                : 'none';

    return {
        userSubscription: patchedSubscription,
        subscriptionState,
        isInFreeTrial,
        isLoading,
    };
}
